import { clamp, evalCurve, meanCurve, type Curve } from './curve'
import { SIDES, cycleTime, footGeometry, gaitLengths, type GaitResult, type Side } from './gait'
import { JOINTS, type JointPlane, type PlaneKey } from './joints'
import { buildSkeleton, poseAt, type PelvisPlacement } from './kinematics'
import { HOP, PICK, cycleSteps, isMove, moveAngle } from './moves'
import type { HumanoidModel } from './model'
import { freezeAngles } from './off'
import type { GaitKind, GaitTrim, SimState } from './state'

/**
 * Der Referenzgang — die aufgezeichneten Kurven, ausgewertet.
 *
 * DER NULLZUSTAND. Der gerechnete Gang entsteht aus einer Aufgabe; dieser hier
 * steht schon da und ist die Vergleichsbasis für alles andere. Er ist keine
 * Zweitrechnung derselben Sache: er kommt aus der Ganganalyse, nicht aus einem
 * Modell, und genau deshalb kann man am Unterschied etwas ablesen.
 *
 * DIE ENTSCHEIDENDE BAUFORM: er liefert dasselbe `GaitResult` wie die Erzeugung.
 * Bodenkontakt, Dynamik und Antriebsauslegung sehen keinen Unterschied und
 * brauchen keinen Sonderweg. Ein Referenzgang, der eine eigene Kette bekäme,
 * wäre eine zweite Wahrheit — und die Zahlen daraus mit denen des gerechneten
 * Gangs zu vergleichen hiesse, zwei verschiedene Rechnungen zu vergleichen
 * statt zwei verschiedene Gänge.
 *
 * VIER EBENEN, IN DIESER REIHENFOLGE:
 *
 *   1. Kurvensatz je Gangart — Gehen, Tragen, Laufen, Sprinten. Wo für eine
 *      Gangart keine eigene Kurve vorliegt, wird die Gehkurve um ihren
 *      Mittelwert gestreckt.
 *   2. Regler — die Schieber verformen die Kurve um ihren MITTELWERT, nicht um
 *      null. Wer um null skaliert, verschiebt die Haltung mit der Amplitude.
 *   3. Gangbild — `mod` ändert einzelne Kanäle, `warp` verzerrt die Zeitachse.
 *   4. Modellstufen — abgeschaltete Anteile fallen auf ihren Mittelwert oder
 *      auf null zurück, je nachdem, ob sie eine Haltung oder eine Bewegung sind.
 */

/* -------------------------------------------------------------------------
   Kurvensatz je Gangart
   ------------------------------------------------------------------------- */

/**
 * Streckfaktoren für Gangarten ohne eigene Kurve.
 *
 * Sie strecken die GEHKURVE um ihren Mittelwert. Das ist eine Behelfslösung und
 * als solche benannt: eine gestreckte Gehkurve ist keine Laufkurve. Wo eigene
 * Linien vorliegen (`run`, `spr`, `car`), werden sie benutzt und diese Faktoren
 * kommen nicht zum Zug.
 */
export const CURVE_SCALE = { run: 1.35, sprint: 1.22, carry: 0.8 } as const

/** Streubreite des Normbands je Gangart — der graue Korridor im Diagramm. */
export const BAND_SCALE = { walk: 1, carry: 0.8, run: 1.6, sprint: 2.1 } as const

const isRun = (kind: GaitKind): boolean => kind === 'run' || kind === 'sprint'

/**
 * Rohwert einer Kurve an der Stelle `x` (0 … 100 % des Zyklus).
 *
 * Die Auswahl des Kurvensatzes steht hier und an keiner zweiten Stelle. Sie ist
 * verschachtelt, weil die Vorlage nicht für jede Gangart jede Kurve hat: der
 * Sprint fällt auf die Laufkurve zurück, die Laufkurve auf die Gehkurve, und
 * jeder Rückfall wird um den Mittelwert gestreckt statt um null.
 */
export function rawAngle(curve: JointPlane, x: number, kind: GaitKind): number {
  if (kind === 'carry') {
    if (curve.car) return evalCurve(curve.car, x)
    return stretch(curve.kf, x, CURVE_SCALE.carry)
  }
  if (!isRun(kind)) return evalCurve(curve.kf, x)

  if (kind === 'sprint') {
    if (curve.spr) return evalCurve(curve.spr, x)
    if (curve.run) return stretch(curve.run, x, CURVE_SCALE.sprint)
    return stretch(curve.kf, x, CURVE_SCALE.run * CURVE_SCALE.sprint)
  }
  if (curve.run) return evalCurve(curve.run, x)
  return stretch(curve.kf, x, CURVE_SCALE.run)
}

/** Um den MITTELWERT strecken — nicht um null. */
const stretch = (curve: Curve, x: number, factor: number): number => {
  const mean = meanCurve(curve)
  return mean + (evalCurve(curve, x) - mean) * factor
}

/** Mittelwert des Kurvensatzes, der für diese Gangart gilt. */
export function curveMean(curve: JointPlane, kind: GaitKind): number {
  if (kind === 'carry') return meanCurve(curve.car ?? curve.kf)
  if (kind === 'sprint' && curve.spr) return meanCurve(curve.spr)
  if (isRun(kind) && curve.run) return meanCurve(curve.run)
  return meanCurve(curve.kf)
}

/** Streubreite des Normbands in Grad. */
export const curveBand = (curve: JointPlane, kind: GaitKind): number =>
  curve.sd * (BAND_SCALE[kind as keyof typeof BAND_SCALE] ?? 1)

/**
 * Die Haltung der Arme beim TRAGEN.
 *
 * DER FEHLER, DEN SIE BEHEBT. Für das Tragen gibt es in der Vorlage keine
 * eigenen Armkurven; `rawAngle` fiel deshalb auf die Gehkurve zurück. Die Arme
 * schwangen also weiter wie beim Gehen, die Hände blieben neben den Hüften —
 * und die Kiste, die zwischen den Handgelenken hängt, hing dort mit. Zu sehen
 * war jemand, der eine Kiste an den Knien vor sich herträgt.
 *
 * WAS STATTDESSEN GILT. Schulter gebeugt, Ellbogen fast rechtwinklig: die
 * Unterarme liegen waagerecht vor dem Körper, die Kiste sitzt an der Brust. Das
 * ist die Haltung, in der ein Mensch eine Kiste über eine Strecke trägt, und
 * sie ist auch die günstige — der Hebelarm zur Hüfte ist so am kürzesten, und
 * genau darum geht es im Kapitel „Tragen".
 *
 * DER REST-SCHWUNG IST NICHT NULL. Die Arme sind fest, aber der Körper bewegt
 * sich unter ihnen; ein völlig starres Paar Arme sieht aus wie eine Schaufel-
 * puppe. Zwölf Prozent des Gehschwungs bleiben — genug, dass es lebt, zu wenig,
 * um die Kiste wandern zu lassen.
 *
 * SIE STEHEN HIER UND NICHT IN DER BÜHNE. Die Armstellung geht über den
 * Schwerpunkt in die Bodenreaktionskraft und über den Hebelarm in die
 * Rumpfmomente ein. Eine Haltung, die nur gezeichnet und nicht gerechnet wird,
 * wäre genau der Fehler, den die Kiste vorher hatte — nur andersherum.
 */
export const CARRY_ARMS = {
  /** Schulterbeugung, Grad. Positiv ist nach vorn. */
  shoulder: 45,
  /** Ellbogenbeugung, Grad. */
  elbow: 85,
  /** Anteil des Gehschwungs, der übrig bleibt. */
  swing: 0.12,
} as const

/* -------------------------------------------------------------------------
   Gangbilder
   ------------------------------------------------------------------------- */

/**
 * Ein Gangbild — eine Abwandlung des Referenzgangs.
 *
 * `params` verstellt Regler, `mod` greift in einzelne Kanäle ein, `warp`
 * verzerrt die Zeitachse. Alle drei sind DATEN dieses Moduls und keine
 * Sonderfälle im Auswertecode: ein neues Gangbild ist ein Eintrag, kein Eingriff.
 */
export interface Preset {
  key: string
  label: string
  note: string
  /** Reglerwerte, die dieses Gangbild setzt. */
  params?: Partial<GaitTrim>
  /** Standphasenanteil, wenn er von dem der Gangart abweicht. */
  stanceFraction?: number
  /** Halbe Spurbreite im Stand, m. */
  standWidth?: number
  /** Verändert einen einzelnen Kanal. */
  mod?: (joint: string, plane: PlaneKey, side: Side, value: number, phase: number) => number
  /** Verzerrt die Zeitachse — für ungleich lange Standphasen. */
  warp?: (t: number) => number
}

/**
 * Sprunggelenkbahn des Videogangs.
 *
 * Hält die Sohle in der Standphase waagerecht; aus der gemessenen Restneigung
 * des Fusses zurückgerechnet.
 */
const VIDEO_ANKLE: Curve = [
  [0, 4],
  [7, 13],
  [13, 18],
  [20, 18],
  [27, 15],
  [35, 12],
  [43, 11],
  [50, 10],
  [57, 6],
  [65, 2],
  [72, 0],
  [85, 0],
]

const WALK_PRESETS: Preset[] = [
  {
    key: 'normal',
    label: 'Normgang',
    note: 'Referenzgang bei 112 spm. Alle Kurven liegen im Normband — das ist die Vergleichsbasis für alle anderen Gangbilder.',
  },
  {
    key: 'stiff',
    label: 'Steifknie-Gang',
    note: 'Rechts: Knieflexion im Schwung stark reduziert. Der Fuss bleibt hängen — kompensiert wird mit Beckenhebung und Zirkumduktion.',
    mod: (joint, plane, side, v, ph) => {
      if (side !== 'R') return v
      if (joint === 'knee' && plane === 'sag') return 4 + (v - 4) * 0.32
      if (joint === 'hip' && plane === 'fro' && ph > 0.6) {
        return v - 9 * Math.sin((Math.PI * (ph - 0.6)) / 0.4)
      }
      if (joint === 'pelvis' && plane === 'fro') return v * 1.55
      return v
    },
  },
  {
    key: 'equinus',
    label: 'Spitzfuss',
    note: 'Rechts: dauerhafte Plantarflexion. Initialkontakt über den Vorfuss statt über die Ferse, das Knie wird in der Standphase nach hinten gedrückt.',
    mod: (joint, plane, side, v, ph) => {
      if (side !== 'R') return v
      if (joint === 'ankle' && plane === 'sag') return clamp(v - 19, -38, 8)
      if (joint === 'knee' && plane === 'sag' && ph < 0.6) return v - 5
      return v
    },
  },
  {
    key: 'trendelenburg',
    label: 'Trendelenburg',
    note: 'Schwäche der rechten Abduktoren: in der rechten Standphase sinkt das Becken zur gesunden linken Seite ab. Deutlich in der Frontalebene.',
    params: { lean: 4 },
    mod: (joint, plane, _side, v, ph) =>
      joint === 'pelvis' && plane === 'fro' && ph < 0.62
        ? v + 11 * Math.sin((Math.PI * ph) / 0.62)
        : v,
  },
  {
    key: 'crouch',
    label: 'Crouch-Gang',
    note: 'Beidseitige Dauerflexion in Hüfte und Knie, vermehrte Dorsalextension. Der Körperschwerpunkt liegt tiefer, die Schrittlänge sinkt.',
    params: { step: 0.72, push: 0.55 },
    mod: (joint, plane, _side, v) => {
      if (plane !== 'sag') return v
      if (joint === 'hip') return v + 17
      if (joint === 'knee') return v + 24
      if (joint === 'ankle') return v + 7
      return v
    },
  },
  {
    key: 'video',
    label: 'Roboter (Video)',
    note: 'Nachgebautes Gangbild aus der Videoaufnahme: rund 125 Schritte/min, kurzer Schritt, Standphase 68 % — beide Füsse stehen 36 % des Zyklus gemeinsam am Boden, gegenüber 24 % beim Normgang. Flacher Fussaufsatz ohne Fersenkontakt, in der Standphase dauerhaft gebeugte Knie, Rumpf fast aufrecht. Die Arme schwingen mit dauerhaft rund 90° gebeugtem Ellbogen. Das ist kein Fehlerbild aus der Klinik, sondern die Signatur einer positionsgeregelten Maschine mit begrenztem Sprunggelenkmoment.',
    stanceFraction: 0.7,
    params: {
      cadence: 125,
      step: 0.42,
      knee: 1,
      push: 0.45,
      pelvis: 0.4,
      tilt: 0.5,
      arm: 0.85,
      lean: -2,
    },
    mod: (joint, plane, _side, v, ph) => {
      if (joint === 'elbow') return 90 + (v - 33) * 0.25
      if (joint === 'shoulder' && plane === 'fro') return v - 4
      if (plane !== 'sag') return v
      if (joint === 'ankle') return clamp(v * 0.22, -5, 5) + evalCurve(VIDEO_ANKLE, ph * 100)
      if (joint === 'mtp') return v * 0.18
      if (joint === 'knee') return v + 14
      if (joint === 'hip') return v + 7
      return v
    },
  },
  {
    key: 'antalgic',
    label: 'Schonhinken',
    note: 'Verkürzte rechte Standphase — der Zyklus wird zeitlich verzerrt. Dazu Rumpfneigung über das belastete Bein und reduzierte Abrollbewegung.',
    params: { lean: 7, asym: 0.16 },
    warp: (t) => t + 0.055 * Math.sin(2 * Math.PI * t),
    mod: (joint, plane, side, v, ph) => {
      if (side === 'R' && plane === 'sag') {
        if (joint === 'ankle') return v * 0.6
        if (joint === 'knee' && ph < 0.6) return v * 0.7
      }
      return v
    },
  },
]

const RUN_PRESETS: Preset[] = [
  {
    key: 'jog',
    label: 'Dauerlauf',
    note: 'Lauf bei 170 spm mit Mittelfussaufsatz. Zwischen den Standphasen liegt eine echte Flugphase — beide Füsse sind in der Luft.',
  },
  {
    key: 'fore',
    label: 'Vorfusslauf',
    note: 'Aufsatz über den Vorfuss: das Sprunggelenk bleibt beim Bodenkontakt plantarflektiert, die Wade übernimmt mehr Dämpfungsarbeit.',
    params: { cadence: 180 },
    mod: (joint, plane, _side, v, ph) =>
      joint === 'ankle' && plane === 'sag' && ph < 0.2 ? v - 12 : v,
  },
  {
    key: 'heel',
    label: 'Fersenlauf',
    note: 'Aufsatz über die Ferse bei gestrecktem Knie. Die Bremskraft am Anfang der Standphase steigt deutlich.',
    params: { cadence: 158, step: 1.12 },
    mod: (joint, plane, _side, v, ph) => {
      if (plane !== 'sag' || ph > 0.22) return v
      if (joint === 'ankle') return v + 14
      if (joint === 'knee') return v - 10
      return v
    },
  },
  {
    key: 'v30',
    label: '3,0 m/s',
    note: 'Auslegungspunkt für das Konzept Gen5, untere Stufe. Bei 180 Schritten je Minute und 1,11-facher Schrittlänge ergibt sich rund 3,0 m/s. Kniehub und Abdruck liegen nur wenig über dem Dauerlauf.',
    params: { cadence: 180, step: 1.11, knee: 1.06, push: 1.04, arm: 1.12, lean: -3 },
  },
  {
    key: 'v40',
    label: '4,0 m/s',
    note: 'Auslegungspunkt für das Konzept Gen5, obere Stufe. 202 Schritte je Minute, deutlich grösserer Kniehub, kräftiger Abdruck und stärkerer Armeinsatz — die Grenze dessen, was mit Katalogaktuatoren in 55 kg schliesst.',
    params: { cadence: 202, step: 1.3, knee: 1.22, push: 1.12, arm: 1.35, lean: -7 },
  },
  {
    key: 'tempo',
    label: 'Tempolauf',
    note: 'Hohe Kadenz, ausgeprägter Kniehub und kräftiger Abdruck — schneller Dauerlauf, noch kein Sprint.',
    params: { cadence: 196, step: 1.25, knee: 1.18, arm: 1.3, lean: -6 },
  },
  {
    key: 'over',
    label: 'Überschritt',
    note: 'Der Fuss setzt zu weit vor dem Körperschwerpunkt auf. Die Bremskraft steigt, das Knie bleibt gestreckt — typischer Fehler beim Laufen.',
    params: { step: 1.3, cadence: 150 },
    mod: (joint, plane, _side, v, ph) => {
      if (plane !== 'sag' || ph > 0.18) return v
      if (joint === 'knee') return v * 0.55
      if (joint === 'ankle') return v + 16
      return v
    },
  },
]

const CARRY_PRESETS: Preset[] = [
  {
    key: 'karton',
    label: 'Umzugskarton',
    note: '40 kg in einer Kiste 80 × 40 × 30 cm, beidhändig vor dem Körper. Der Rumpf lehnt zurück, bis der gemeinsame Schwerpunkt wieder über der Standfläche liegt. Armschwung entfällt, die Spurbreite wächst.',
  },
  {
    key: 'nah',
    label: 'Eng am Körper',
    note: 'Die Kiste liegt dicht am Bauch. Kurzer Lasthebel zur Hüfte, kleinere Rumpf- und Hüftmomente — die empfohlene Trageweise.',
    mod: (joint, plane, _side, v) => {
      if (joint === 'elbow') return v + 20
      if (joint === 'shoulder') return plane === 'sag' ? v - 4 : plane === 'fro' ? v - 8 : v
      return v
    },
  },
  {
    key: 'weit',
    label: 'Weit vorn',
    note: 'Die Kiste hängt mit gestreckteren Armen weit vorn. Der Lasthebel wächst, Rücklage und Lendenmoment steigen deutlich.',
    params: { cadence: 100, step: 0.9 },
    mod: (joint, plane, _side, v) => {
      if (joint === 'elbow') return v - 20
      if (joint === 'shoulder' && plane === 'sag') return v + 5
      return v
    },
  },
  {
    key: 'hoch',
    label: 'Hoch getragen',
    note: 'Die Kiste sitzt auf Brusthöhe. Der Hebel zur Hüfte ist kurz, der Schwerpunkt aber hoch — das Gleichgewicht wird empfindlicher.',
    params: { cadence: 108 },
    mod: (joint, plane, _side, v) => {
      if (joint === 'elbow') return v + 16
      if (joint === 'shoulder') return plane === 'sag' ? v + 16 : plane === 'fro' ? v - 6 : v
      return v
    },
  },
  {
    key: 'eilig',
    label: 'Zügig',
    note: 'Höheres Tempo unter Last. Bodenreaktionskraft und Gelenkmomente steigen, die Doppelunterstützung wird kürzer.',
    params: { cadence: 120, step: 1.08 },
  },
]

const SPRINT_PRESETS: Preset[] = [
  {
    key: 'vmax',
    label: 'Höchstgeschwindigkeit',
    note: 'Sprint bei 4,0 m/s und 240 Schritten je Minute. Bodenkontakt nur noch 21 % des Zyklus, über die Hälfte der Zeit ist kein Fuss am Boden. Vorfussaufsatz, Knie schlägt im Schwung bis an die Gesässmuskulatur an.',
  },
  {
    key: 'antritt',
    label: 'Antritt',
    note: 'Erste Schritte aus der Beschleunigung: Rumpf stark vorgeneigt, kurze Schritte, sehr langer Abdruck nach hinten. Die Bremskraft am Aufsatz fehlt fast völlig.',
    params: { cadence: 216, lean: -10 },
    mod: (joint, plane, _side, v, ph) =>
      plane === 'sag' && ph < 0.22 && joint === 'hip' ? v - 10 : v,
  },
  {
    key: 'steig',
    label: 'Steigerungslauf',
    note: 'Kontrolliertes Tempo unterhalb des Maximums. Etwas längere Bodenkontakte, geringere Kniehubhöhe, ruhigerer Rumpf.',
    params: { cadence: 204, knee: 0.9 },
  },
  {
    key: 'mued',
    label: 'Ermüdung',
    note: 'Später Sprintabschnitt: Kniehub und Abdruck lassen nach, die Kadenz bleibt hoch. Der Schritt wird kürzer, der Bodenkontakt länger.',
    params: { cadence: 220, knee: 0.72, push: 0.78 },
    mod: (joint, plane, _side, v, ph) =>
      joint === 'hip' && plane === 'sag' && ph > 0.5 ? v * 0.86 : v,
  },
]

const STAND_PRESETS: Preset[] = [
  {
    key: 'neutral',
    label: 'Neutralstand',
    note: 'Ruhiger beidbeiniger Stand, Füsse hüftbreit. Beide Füsse tragen dauerhaft je die Hälfte des Gewichts, es gibt keine Schwungphase. Der Druckmittelpunkt liegt zwischen den Füssen und wandert nur im Millimeterbereich.',
    standWidth: 0.085,
  },
  {
    key: 'breit',
    label: 'Breiter Stand',
    note: 'Schulterbreiter bis breiter Stand. Die Unterstützungsfläche wird seitlich grösser — mehr Reserve gegen Kippen zur Seite, dafür stehen die Hüftabduktoren unter Dauerlast.',
    standWidth: 0.15,
    mod: (joint, plane, _side, v) => (joint === 'hip' && plane === 'fro' ? v - 5 : v),
  },
  {
    key: 'eng',
    label: 'Enger Stand',
    note: 'Füsse dicht nebeneinander. Die seitliche Unterstützungsfläche schrumpft auf Fussbreite — der Stand wird empfindlich gegen jede Querstörung. Testfall für die Regelung.',
    standWidth: 0.04,
    mod: (joint, plane, _side, v) => (joint === 'hip' && plane === 'fro' ? v + 4 : v),
  },
  {
    key: 'beuge',
    label: 'Halbe Kniebeuge',
    note: 'Knie und Sprunggelenk gebeugt, Rumpf aufrecht über der Fussmitte. Typische Bereitschaftsstellung: tiefer Schwerpunkt, grosse Regelreserve — aber die Kniemotoren halten dauerhaft ein Moment.',
    standWidth: 0.095,
    mod: (joint, plane, _side, v) => {
      if (plane !== 'sag') return v
      /* Hüfte und Sprunggelenk so, dass die Sohle flach bleibt und der
         Schwerpunkt mittig steht — 30° Kniebeugung. */
      if (joint === 'knee') return v + 26
      if (joint === 'hip') return v + 13.5
      if (joint === 'ankle') return v + 12.4
      return v
    },
  },
]

/**
 * Gangbilder je Bewegungsart.
 *
 * Hackensprung und Aufheben haben je EINEN Eintrag und keine Auswahl. Sie sind
 * Skripte und keine Gangaufgaben: es gibt keine Schrittlänge, die man länger
 * machen, und keine Kadenz, die man wählen könnte, ohne die Bewegung zu einer
 * anderen zu machen. Ein Gangbild wäre hier eine Auswahl ohne Alternative.
 *
 * Bis hierher zeigte `hop` auf WALK_PRESETS — die Gehgangbilder, mit
 * Schrittlängen und Standphasenanteilen, die für einen beidbeinigen Sprung
 * nichts bedeuten.
 */
/**
 * Die Bewegungen mit Namen und Kurzbeschreibung.
 *
 * EINE LISTE UND NICHT DREI. Bis hierher standen die Beschriftungen zweimal in
 * der Oberfläche — einmal auf der Parameterseite, einmal im Spiel — und wer
 * eine Bewegung ergänzte, traf die zweite Stelle nicht. Sie gehören zum
 * Rechenkern, weil sie zu `GaitKind` gehören: eine Gangart ohne Namen lässt
 * sich nicht anbieten.
 */
export interface GaitEntry {
  key: GaitKind
  label: string
  note: string
  /** Gang oder Haltung — die Gliederung der Auswahl. */
  group: 'Gänge' | 'Haltung und Skripte'
}

export const GAIT_LIST: readonly GaitEntry[] = [
  { key: 'walk', label: 'Gehen', note: 'Immer ein Fuss am Boden', group: 'Gänge' },
  { key: 'carry', label: 'Tragen', note: 'Zuladung vor dem Körper', group: 'Gänge' },
  { key: 'run', label: 'Laufen', note: 'Mit Flugphase', group: 'Gänge' },
  { key: 'sprint', label: 'Sprinten', note: 'Kurzer Bodenkontakt', group: 'Gänge' },
  { key: 'stand', label: 'Stehen', note: 'Kein Schritt', group: 'Haltung und Skripte' },
  {
    key: 'hop',
    label: 'Hackensprung',
    note: 'Beide Beine gleichphasig, mit Flugphase',
    group: 'Haltung und Skripte',
  },
  {
    key: 'pick',
    label: 'Aufheben',
    note: 'Kniestand, beide Füsse tragen',
    group: 'Haltung und Skripte',
  },
] as const

export const PRESETS: Record<GaitKind, Preset[]> = {
  walk: WALK_PRESETS,
  carry: CARRY_PRESETS,
  run: RUN_PRESETS,
  sprint: SPRINT_PRESETS,
  stand: STAND_PRESETS,
  hop: [{ key: 'hop', label: HOP.label, note: HOP.note, stanceFraction: HOP.stance }],
  pick: [{ key: 'pick', label: PICK.label, note: PICK.note, stanceFraction: PICK.stance }],
}

export const presetFor = (kind: GaitKind, key: string): Preset =>
  PRESETS[kind].find((p) => p.key === key) ?? PRESETS[kind][0]

/**
 * Standphasenanteil des Referenzgangs.
 *
 * Aus dem Gangbild, wenn es einen eigenen nennt; sonst aus der Gangart. Diese
 * Zahlen sind gemessen und nicht gewählt — sie gehören zur Aufzeichnung.
 */
export const STANCE_FRACTION: Record<GaitKind, number> = {
  walk: 0.62,
  carry: 0.66,
  run: 0.3,
  sprint: 0.22,
  stand: 1,
  /* Aus der Flugphase des Skripts und nicht geschätzt: 0,34 bis 0,66 des Zyklus
     ist die Maschine in der Luft, also trägt jedes Bein 68 % der Zeit. Beide
     zugleich — das ist der Unterschied zum Gang. */
  hop: HOP.stance,
  pick: PICK.stance,
}

export function stanceFraction(state: SimState): number {
  /* Skriptbewegungen haben keine Gangaufgabe — auch nicht im gerechneten
     Betrieb. Ihr Standphasenanteil steht im Skript. */
  if (isMove(state.gait)) return STANCE_FRACTION[state.gait]
  if (state.generated && state.gait !== 'stand') return state.task.duty
  const preset = presetFor(state.gait, state.preset)
  return preset.stanceFraction ?? STANCE_FRACTION[state.gait]
}

/* -------------------------------------------------------------------------
   Der ruhige Stand
   ------------------------------------------------------------------------- */

/**
 * Gelenkwinkel der Normalhaltung, Grad.
 *
 * Leichte Beckenanteversion, Knie 3° Restflexion, Sprunggelenk neutral, Arme
 * locker am Körper. Stehen kennt keine Schrittlänge — deshalb feste Werte statt
 * Kurven.
 */
export const STAND_POSE: Record<string, Partial<Record<PlaneKey, number>>> = {
  pelvis: { sag: -3, fro: 0, tra: 0 },
  hip: { sag: -2, fro: -1, tra: 0 },
  knee: { sag: 4, fro: 0, tra: 0 },
  ankle: { sag: 7.2, fro: 0, tra: 0 },
  mtp: { sag: 0 },
  lumbar: { sag: 0, fro: 0, tra: 0 },
  thorax: { sag: 0, fro: 0, tra: 0 },
  shoulder: { sag: -3, fro: -7, tra: 2 },
  elbow: { sag: 12 },
}

/* -------------------------------------------------------------------------
   Regler
   ------------------------------------------------------------------------- */

/**
 * Dämpfung der Beckenkanäle bei hoher Kadenz.
 *
 * DIE KORREKTUR, OHNE DIE DER SPRINT UNSINN LIEFERT.
 *
 * Die aufgezeichnete Beckenschräge und Beckendrehung stammen aus dem Gehen bei
 * rund 112 Schritten je Minute. Mit unveränderter Amplitude bei 240 Schritten
 * gefahren, schwingt das Becken mit der doppelten Frequenz; die seitliche
 * Beschleunigung wächst mit dem QUADRAT und mit ihr die Querkraft am Boden. In
 * der Vorlage kamen so 1269 N Querkraft und 1075 Nm Rollmoment an der Hüfte
 * zustande — erwartbar wären rund 100.
 *
 * Die Amplitude wird deshalb mit (T/T₀)² gedämpft: dieselbe Behandlung, die die
 * seitliche Beckenverlagerung im Kontaktmodell ohnehin bekommt (A = a/ω²).
 */
export function frontalDamping(state: SimState): number {
  if (state.generated || state.gait === 'stand' || state.gait === 'hop') return 1
  const k = cycleTime(state.trim.cadence) / cycleTime(112)
  return Math.min(1, k * k)
}

/**
 * Die Regler verformen die Kurve um ihren MITTELWERT.
 *
 * Wer stattdessen um null skaliert, verschiebt mit der Amplitude auch die
 * Haltung: ein halbierter Kniewinkel hiesse dann nicht „weniger Beugung", sondern
 * „das halbe Knie steht woanders". Beim Sprunggelenk ist es umgekehrt — dort
 * trennt die Vorlage nach Vorzeichen, weil Abdruck und Anhebung verschiedene
 * Bewegungen mit verschiedenen Reglern sind.
 */
export function shapeParam(
  joint: string,
  plane: PlaneKey,
  side: Side,
  value: number,
  mean: number,
  trim: GaitTrim,
  damping: number,
): number {
  const asym = side === 'R' ? 1 - trim.asym : 1 + trim.asym

  if (plane === 'sag') {
    if (joint === 'hip') return mean + (value - mean) * trim.step * asym
    if (joint === 'knee') return mean + (value - mean) * trim.knee * asym
    if (joint === 'ankle') return value < 0 ? value * trim.push : value * (0.4 + 0.6 * trim.step)
    if (joint === 'shoulder' || joint === 'elbow') return mean + (value - mean) * trim.arm
    if (joint === 'pelvis') return mean + (value - mean) * trim.pelvis * trim.tilt
    return value
  }

  if (joint === 'pelvis') return value * trim.pelvis * damping
  if (joint === 'shoulder') return mean + (value - mean) * trim.arm
  return value
}

/* -------------------------------------------------------------------------
   Die Auswertung
   ------------------------------------------------------------------------- */

/**
 * Ein Winkel des Referenzgangs, in Grad.
 *
 * Die Reihenfolge ist nicht beliebig: erst die Modellstufe (soll dieser Anteil
 * überhaupt mitwirken?), dann die Zeitverzerrung, dann der Kurvenwert, dann die
 * Regler, zuletzt das Gangbild. Wer die Regler nach dem Gangbild anwendet,
 * skaliert dessen Eingriff mit — und ein Steifknie-Gang mit halber Knieamplitude
 * wäre plötzlich kein Steifknie-Gang mehr.
 */
export function referenceAngle(
  state: SimState,
  joint: string,
  plane: PlaneKey,
  side: Side,
  t: number,
): number {
  const definition = JOINTS[joint]
  const curve = definition?.planes[plane]
  if (!curve) return 0

  const preset = presetFor(state.gait, state.preset)
  const mean = curveMean(curve, state.gait)

  if (state.gait === 'stand') {
    const value = STAND_POSE[joint]?.[plane] ?? 0
    return preset.mod ? preset.mod(joint, plane, side, value, 0) : value
  }

  /*
    SKRIPTBEWEGUNGEN GEHEN EINEN EIGENEN WEG — vor den Modellstufen und vor der
    Zeitachse. Beides passt nicht auf sie: die Modellstufen greifen in eine
    Normkurve ein, die es hier nicht gibt, und die Zeitachse verschiebt die
    linke Seite um eine halbe Periode, was beim Hackensprung genau falsch ist.

    Was von den Modellstufen sinnvoll bleibt, steht unten in `moveFeatures`.
  */
  if (isMove(state.gait)) {
    return moveFeatures(state, joint, plane, moveAngle(state.gait, joint, plane, side, t))
  }

  /* --- Modellstufen ------------------------------------------------------
     Abgeschaltete Anteile fallen auf ihren MITTELWERT zurück, wenn sie eine
     Haltung beschreiben, und auf NULL, wenn sie eine Bewegung sind. Wer beides
     auf null setzt, lässt den Roboter beim Abschalten der Arme die Schultern
     hochreissen. */
  const features = state.features
  if (joint === 'pelvis') {
    const on =
      plane === 'sag' ? features.pelSag : plane === 'fro' ? features.pelFro : features.pelTra
    if (!on) return plane === 'sag' ? mean : 0
  }
  if (joint === 'mtp' && !features.mtp) return 0
  if ((joint === 'lumbar' || joint === 'thorax') && !features.trunk) return 0
  if ((joint === 'shoulder' || joint === 'elbow') && !features.arms) return mean
  if (!features.frontal && plane !== 'sag' && ['hip', 'knee', 'ankle'].includes(joint)) return 0

  /* --- Zeitachse ---------------------------------------------------------- */
  let phase = preset.warp ? ((preset.warp(t) % 1) + 1) % 1 : t
  if (side === 'L' && definition.sided) phase = (phase + 0.5) % 1

  /* --- Kurve, Regler, Gangbild -------------------------------------------- */
  const raw = rawAngle(curve, phase * 100, state.gait)
  const shaped = shapeParam(joint, plane, side, raw, mean, state.trim, frontalDamping(state))
  return preset.mod ? preset.mod(joint, plane, side, shaped, phase) : shaped
}

/**
 * Was von den Modellstufen auf eine Skriptbewegung anwendbar ist.
 *
 * NICHT ALLE. Die Stufen der Gangkette greifen in Normkurven ein und fallen auf
 * deren MITTELWERT zurück; ein Skript hat keinen Mittelwert, den es sinnvoll
 * halten könnte — sein Nullwert IST die Grundhaltung, weil es von dort ausgeht
 * und dorthin zurückkehrt.
 *
 * Deshalb hier nur die Stufen, deren Bedeutung sich überträgt: eine Ebene ganz
 * wegnehmen, eine Kette stilllegen, einen Anteil abschalten. `pelSag` fehlt
 * bewusst — bei Hackensprung und Aufheben ist die Beckenkippung nicht ein
 * Anteil der Bewegung, sondern ein Teil davon, und ohne sie wäre es eine andere
 * Bewegung statt derselben ohne einen Anteil.
 */
function moveFeatures(state: SimState, joint: string, plane: PlaneKey, value: number): number {
  const f = state.features
  if (joint === 'pelvis' && plane === 'fro' && !f.pelFro) return 0
  if (joint === 'pelvis' && plane === 'tra' && !f.pelTra) return 0
  if (joint === 'mtp' && !f.mtp) return 0
  if ((joint === 'lumbar' || joint === 'thorax') && !f.trunk) return 0
  if ((joint === 'shoulder' || joint === 'elbow') && !f.arms) return 0
  if (!f.frontal && plane !== 'sag' && ['hip', 'knee', 'ankle'].includes(joint)) return 0
  return value
}

/** Flugzeit je Abschnitt, s. Null, wenn dauernd ein Fuss trägt. */
export function flightTime(kind: GaitKind, stance: number, cycle: number): number {
  const steps = cycleSteps(kind)
  const support = stance * steps
  return support < 0.999 ? (1 - support) * (cycle / steps) : 0
}

/** Der reine Normwert, ohne Regler und ohne Gangbild — für den Bandvergleich. */
export function normAngle(
  kind: GaitKind,
  joint: string,
  plane: PlaneKey,
  side: Side,
  t: number,
): number {
  const definition = JOINTS[joint]
  const curve = definition?.planes[plane]
  if (!curve) return 0
  const phase = side === 'L' && definition.sided ? (t + 0.5) % 1 : t
  return rawAngle(curve, phase * 100, kind)
}

/* -------------------------------------------------------------------------
   Als GaitResult
   ------------------------------------------------------------------------- */

/**
 * Der Referenzgang in derselben Form wie der gerechnete.
 *
 * Damit sehen Bodenkontakt, Dynamik und Antriebsauslegung keinen Unterschied.
 * `hipTrack` bleibt null: der Referenzgang gibt KEINE Beckenbahn vor — er ist
 * eine Winkelaufzeichnung, und wo der Körper dabei steht, folgt aus dem
 * Bodenkontakt. Genau darin unterscheidet er sich vom gerechneten Gang, und
 * genau deshalb darf hier keine Bahn erfunden werden.
 */
export function referenceGait(model: HumanoidModel, state: SimState): GaitResult {
  const samples = state.samples
  const cycle = cycleTime(state.trim.cadence)
  const lengths = gaitLengths(model, state.task)
  const foot = footGeometry(model)

  const angles: GaitResult['angles'] = {}
  for (const joint of Object.keys(JOINTS)) {
    const planes: Partial<Record<PlaneKey, Record<Side, Float32Array>>> = {}
    for (const plane of Object.keys(JOINTS[joint].planes) as PlaneKey[]) {
      const table = {
        R: new Float32Array(samples + 1),
        L: new Float32Array(samples + 1),
      }
      for (let i = 0; i <= samples; i++) {
        const t = (i % samples) / samples
        for (const side of SIDES) {
          table[side][i] = referenceAngle(state, joint, plane, side, t)
        }
      }
      planes[plane] = table
    }
    angles[joint] = planes
  }

  const stance = stanceFraction(state)
  const speed = referenceSpeed(model, state)

  return {
    angles,
    /* Keine vorgegebene Beckenbahn — siehe oben. */
    hipTrack: new Float32Array(samples + 1),
    lengths,
    foot,
    samples,
    cycleTime: cycle,
    report: {
      clipHigh: 0,
      clipLow: 0,
      reach: 0,
      speed,
      swingBlend: 1,
      /* Flugzeit je Abschnitt. Beim Gang hat ein Zyklus zwei Abschnitte und
         beide Beine tragen zusammen `2 × Standphasenanteil`; bei gleichphasigen
         Beinen gibt es einen Abschnitt, und die Tragzeit ist der Anteil selbst.
         Ohne diese Unterscheidung meldete der Hackensprung null Flugzeit. */
      flight: flightTime(state.gait, stance, cycle),
      rise: 0,
      spring: 0,
      /* Der Referenzgang gibt Winkel vor und keine Fussspur; ein Abstand zur
         Mittellinie folgt daraus erst über die Kinematik. Null heisst hier
         „nicht gemessen" und nicht „der Fuss steht auf der Mittellinie". */
      trackClearance: 0,
      warn: false,
    },
  }
}

/**
 * Fortbewegungsgeschwindigkeit des Referenzgangs.
 *
 * DIE HERLEITUNG IST DER PUNKT. Beim Referenzgang ist die Geschwindigkeit keine
 * Eingabe — sie steckt in den Winkeln und muss herausgeholt werden.
 *
 * Der Schlüssel: **solange ein Fuss am Boden steht, wandert er gegenüber dem
 * Körper genau mit der Fortbewegungsgeschwindigkeit nach hinten.** Gemessen wird
 * deshalb die mittlere Rückwärtsgeschwindigkeit der Sprunggelenkmitte während
 * der Standphase — nicht die Auslenkung geteilt durch die Zeit.
 *
 * Warum nicht einfacher: über die Hüftbeugung liesse sich eine Schrittlänge
 * schätzen, aber Knie, Sprunggelenk und Beckendrehung tragen mit bei, je nach
 * Gangbild verschieden stark. Über die Auslenkung des Fusses ginge es auch,
 * dann müsste man aber durch den Standphasenanteil teilen und hätte die
 * Fussabrollung unterschlagen. Die mittlere Geschwindigkeit hat beides schon
 * drin.
 */
export function referenceSpeed(model: HumanoidModel, state: SimState): number {
  /*
    STEHEN, HACKENSPRUNG UND AUFHEBEN KOMMEN NICHT VON DER STELLE — das steht in
    ihrer Bewegung und muss nicht gemessen werden.

    Es zu messen ergäbe sogar eine falsche Zahl: die Messung nimmt die mittlere
    Rückwärtsgeschwindigkeit der Sprunggelenkmitte in der Standphase, und bei
    einer ortsfesten Bewegung ist das Rauschen um null. Der Hackensprung meldete
    damit −0,06 m/s und einen Doppelschritt von −7 cm.
  */
  if (state.gait === 'stand' || isMove(state.gait)) return 0

  const skeleton = buildSkeleton(model)
  const rest: PelvisPlacement = { x: 0, y: skeleton.pelvisRest, z: 0 }
  const stance = stanceFraction(state)
  const n = 120
  const dt = cycleTime(state.trim.cadence) / n

  const x = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    const t = i / n
    const pose = poseAt(
      skeleton,
      (joint, plane, side) => referenceAngle(state, joint, plane, side, t),
      rest,
    )
    x[i] = pose.ankle.R.position.x
  }

  /* Nur die MITTE der Standphase zählt: an ihren Rändern rollt der Fuss über
     Ferse und Ballen ab, und dabei wandert die Sprunggelenkmitte zusätzlich. */
  const from = Math.round(0.2 * stance * n)
  const to = Math.round(0.8 * stance * n)
  let sum = 0
  let count = 0
  for (let i = from; i < to; i++) {
    sum -= (x[(i + 1) % n] - x[(i - 1 + n) % n]) / (2 * dt)
    count++
  }
  return count > 0 ? sum / count : 0
}

/** Doppelschrittlänge in Metern — Geschwindigkeit mal Zyklusdauer. */
export const strideLength = (model: HumanoidModel, state: SimState): number =>
  referenceSpeed(model, state) * cycleTime(state.trim.cadence)

/**
 * Der Gang, den der Zustand verlangt — gerechnet oder aufgezeichnet.
 *
 * Die eine Stelle, an der zwischen beiden gewählt wird. Alles dahinter kennt den
 * Unterschied nicht mehr.
 *
 * HIER WERDEN AUCH DIE STILLGELEGTEN ACHSEN EINGEFROREN, und zwar NACH der
 * Erzeugung. Der Gang wurde für die freie Maschine gerechnet; wer eine Achse
 * abschaltet, fragt „was passiert, wenn diese Achse ausfällt" — nicht „wie
 * liefe eine Maschine, die von vornherein ohne sie entworfen wurde". Was der
 * eingefrorene Winkel am Boden anrichtet, meldet der Bodenkontakt danach als
 * Durchdringung oder Schweben, und das ist die ehrliche Auskunft.
 */
export function gaitFor(
  model: HumanoidModel,
  state: SimState,
  generate: (model: HumanoidModel, state: SimState) => GaitResult,
): GaitResult {
  const gait =
    state.generated && state.gait !== 'stand'
      ? generate(model, state)
      : referenceGait(model, state)

  const angles = freezeAngles(gait.angles, state.off)
  return angles === gait.angles ? gait : { ...gait, angles }
}
